import * as React from "react";
import Marquee from "react-marquee-slider";
import styles from "./styles.module.scss";
import { MemoMessageModal as MessageModal } from "../../modals/MessageModal";
import Dialog from "../../Dialog";
import { useQuery } from "@apollo/client";
import { GetApprovedMessages } from "@src/graphql/queries";
import { getCookie } from "cookies-next";
import { motion } from "framer-motion";
import {
  EventBySlugQuery,
  GetApprovedMessagesQuery,
  GetApprovedMessagesQueryVariables,
} from "@src/@types/api.d";
import { ApprovedMessage } from "@src/components/BirthdayTemplate1/MarqueeComp";

type Props = {
  eventId: number;
  slugData: EventBySlugQuery | undefined;
  albumDetailsData?: any;
};

const MarqueeComp: React.FC<Props> = ({
  slugData,
  eventId,
  albumDetailsData,
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [show, setShow] = React.useState<boolean>(false);
  const currentAccessToken = getCookie("tsl") as string;
  const [isMarqueePaused, setIsMarqueePaused] = React.useState(false);

  const pauseMarquee = () => setIsMarqueePaused(true);
  const resumeMarquee = () => setIsMarqueePaused(false);

  React.useEffect(() => {
    setShow(true);
  }, []);

  const mediaData = albumDetailsData?.getMedia || [];
  const [randomBgImage, setRandomBgImage] = React.useState<string | null>(null);

  const isValidImageUrl = (img: any): boolean =>
    img &&
    img.meta &&
    typeof img.meta.s3_url === "string" &&
    img.meta.s3_url.trim() !== "" &&
    img.meta.s3_url.startsWith("http");

  React.useEffect(() => {
    const validImages = mediaData.filter(isValidImageUrl);
    if (validImages.length > 0) {
      const randomIndex = Math.floor(Math.random() * validImages.length);
      const randomImage = validImages[randomIndex];
      if (randomImage?.meta?.s3_url) {
        setRandomBgImage(randomImage.meta.s3_url);
      }
    }
  }, [mediaData]);

  const { data: getApprovedMessagesData } = useQuery<
    GetApprovedMessagesQuery,
    GetApprovedMessagesQueryVariables
  >(GetApprovedMessages, {
    skip: !eventId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { eventId },
    pollInterval: 1000 * 60,
  });

  const messagesToDisplay =
    getApprovedMessagesData?.getApprovedMessages &&
    getApprovedMessagesData.getApprovedMessages.length > 0 &&
    getApprovedMessagesData.getApprovedMessages;

  const defaultMessages: Partial<ApprovedMessage>[] = [
    {
      content: `Welcome to ${
        slugData?.eventBySlug?.name || "this special celebration"
      }! 🎉`,
    },
    {
      content:
        "Share your heartfelt wishes and make this moment unforgettable ✨",
    },
    {
      content: "Your beautiful messages will light up this celebration 💫",
    },
    {
      content: "Be the first to drop a special note and spread some joy! 🌟",
    },
    {
      content:
        "Every celebration is better with warm wishes from loved ones 💝",
    },
    {
      content: "Join us in making this day extra special with your words 🥳",
    },
  ];

  // Helper function to ensure at least 6 entries
  const repeatEntries = (
    entries: (ApprovedMessage | null | Partial<ApprovedMessage>)[] | null
  ): (ApprovedMessage | null | Partial<ApprovedMessage>)[] => {
    console.log(entries?.length);
    if (!entries || entries.length === 0) return [];
    if (entries.length >= 6) return entries;

    const length = entries.length;
    let repeated: (ApprovedMessage | null | Partial<ApprovedMessage>)[] = [];

    if (length === 1) {
      repeated = Array(6).fill(entries[0]);
    } else if (length === 2) {
      repeated = [...entries, ...entries, ...entries];
    } else if (length === 3) {
      repeated = [...entries, ...entries];
    } else if (length === 4) {
      repeated = [...entries, ...entries];
    } else if (length === 5) {
      repeated = [...entries, ...entries];
    }

    return repeated;
  };

  const displayMessages = messagesToDisplay || defaultMessages;

  const evenMessages = repeatEntries(
    displayMessages.filter((_, i) => i % 2 === 0)
  );
  const oddMessages = repeatEntries(
    displayMessages.filter((_, i) => i % 2 !== 0)
  );

  return (
    <div
      className="py-8 sm:py-12 relative overflow-hidden"
      style={{
        backgroundImage: randomBgImage ? `url('${randomBgImage}')` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {randomBgImage && <div className="absolute inset-0 bg-black/30"></div>}

      <div className="relative z-10">
        <div
          className={styles["home__marquee"]}
          onMouseEnter={pauseMarquee}
          onMouseLeave={resumeMarquee}
          onClick={pauseMarquee}
        >
          {/* First Marquee Row */}
          <div style={{ height: "250px", maxWidth: "100vw", width: "100%" }}>
            <Marquee
              velocity={isMarqueePaused ? 0 : 40}
              direction="rtl"
              scatterRandomly={false}
              resetAfterTries={200}
              onInit={() => null}
              onFinish={() => null}
            >
              {evenMessages.map((item, index) => (
                <div
                  key={`quotes-${index}`}
                  className={styles["home__special-note-box"]}
                >
                  <p
                    className={`${styles["home__special-note-text"]} font-ebGaramond`}
                  >
                    {item?.content ?? ""}
                  </p>
                </div>
              ))}
            </Marquee>
          </div>

          {/* Second Marquee Row */}
          {displayMessages.length > 1 && (
            <div style={{ height: "250px", maxWidth: "100vw", width: "100%" }}>
              <Marquee
                velocity={isMarqueePaused ? 0 : 40}
                direction="rtl"
                scatterRandomly={false}
                resetAfterTries={200}
                onInit={() => null}
                onFinish={() => null}
              >
                {oddMessages.map((item, index) => (
                  <div
                    key={`quotes-2-${index}`}
                    className={styles["home__special-note-box"]}
                  >
                    <p
                      className={`${styles["home__special-note-text"]} font-ebGaramond`}
                    >
                      {item?.content ?? ""}
                    </p>
                  </div>
                ))}
              </Marquee>
            </div>
          )}
        </div>

        {show && (
          <div className="text-center mt-6">
            {!messagesToDisplay && (
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-white mb-2 font-ebGaramond">
                  Be the First to Share Your Wishes! 🌟
                </h3>
                <p className="text-gray-200 font-neue">
                  Drop a heartfelt message to make this celebration even more
                  special
                </p>
              </div>
            )}

            {/* Show button only for authenticated users */}
            {!!currentAccessToken && (
              <Dialog
                trigger={
                  <div
                    className={`${
                      styles["home__special-note__btn"]
                    } font-ebGaramond ${
                      !messagesToDisplay
                        ? " text-white bg-black/20 backdrop-blur-sm border border-white/30 px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl hover:bg-white/20 transform hover:scale-105 cursor-pointer transition-all"
                        : ""
                    }`}
                  >
                    {!messagesToDisplay
                      ? "🎉 Share Your Wishes Now!"
                      : "Send a Special Note"}
                  </div>
                }
                open={open}
                onOpenChange={setOpen}
              >
                <MessageModal
                  name={slugData?.eventBySlug?.name ?? ""}
                  eventId={eventId}
                  setOpen={setOpen}
                />
              </Dialog>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarqueeComp;
